/**
 * Phase 41 (Plan 41-03 Task 1) — TanStack Query wrappers for the
 * notifications surface.
 *
 *   useNotifications(params) — list + unreadCount, polls every 60s
 *   useMarkNotificationRead — single mark-read mutation
 *   useMarkAllNotificationsRead — bulk mark-all-read mutation
 *
 * Polling cadence mirrors `useApprovalCounts` (60_000ms refetch interval) so
 * the bell badge in the v7 TopBar stays current without overwhelming the
 * backend. Mutations invalidate the entire `['notifications']` key family so
 * any list page (Surface 6 — plan 41-09) re-renders alongside the dropdown.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  type NotificationListParams,
} from '../services/api/notifications';

export function useNotifications(params?: NotificationListParams) {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => fetchNotifications(params),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}
